import { useState, useEffect, useMemo, useRef } from 'react';
import { useApp } from '../../contexts/AppContext';
import { supabase } from '../../supabaseClient';

const ACCENT = '#00e5a0';

export default function TrackingPage() {
  const { movements, assets, facilities, antennas, profile } = useApp();

  // Build antenna lookup: facilityId + port → location name
  const antennaLabel = useMemo(() => {
    const map = {};
    antennas.forEach(a => {
      // key by facility_id + antenna_port
      const key = `${a.facility_id}_${a.antenna_port}`;
      map[key] = a.location || `ANT${a.antenna_port}`;
      // also key by just antenna_port as fallback
      if (!map[`_${a.antenna_port}`]) map[`_${a.antenna_port}`] = a.location || `ANT${a.antenna_port}`;
    });
    return map;
  }, [antennas]);

  // Resolve human-readable antenna location for a movement
  const getAntennaName = (m) => {
    if (!m.antenna_port) return null;
    // Use stored antenna_name first (bridge looked it up at scan time)
    if (m.antenna_name) return m.antenna_name;
    // Fallback: look up from antennas in memory
    const byFacPort = antennaLabel[`${m.to_facility}_${m.antenna_port}`];
    if (byFacPort) return byFacPort;
    const byPort = antennaLabel[`_${m.antenna_port}`];
    if (byPort) return byPort;
    return `ANT${m.antenna_port}`;
  };

  const [filter,       setFilter]       = useState('all');  // all | known | unknown
  // Reset visible count on filter change
  const handleSetFilter = (v) => { setFilter(v); setVisibleCount(50); };
  const handleSetSearch = (v) => { setSearch(v); setVisibleCount(50); };
  const [facilityFilter, setFacilityFilter] = useState('all');
  const [search,       setSearch]       = useState('');
  const [unknownTags,  setUnknownTags]  = useState([]);
  const [bridgeStatus, setBridgeStatus] = useState(null);
  const [assetMovements, setAssetMovements] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [liveCount,    setLiveCount]    = useState(0);
  const [visibleCount, setVisibleCount] = useState(50);
  const bridgeUrl = localStorage.getItem('rfid_bridge_url') || 'http://localhost:3500';
  const feedRef  = useRef(null);

  // Load asset_movements from Supabase
  useEffect(() => {
    if (!profile?.org_id) return;
    loadData();
    const unsub = subscribeRealtime();
    fetchBridgeStatus();
    const t = setInterval(fetchBridgeStatus, 5000);
    return () => { unsub(); clearInterval(t); };
  }, [profile?.org_id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [{ data: mvs }, { data: unk }] = await Promise.all([
        supabase.from('asset_movements')
          .select('*')
          .eq('org_id', profile.org_id)
          .order('detected_at', { ascending: false })
          .limit(200),
        supabase.from('unknown_tags')
          .select('*')
          .eq('org_id', profile.org_id)
          .order('last_seen_at', { ascending: false })
          .limit(100),
      ]);
      if (mvs) setAssetMovements(mvs);
      if (unk) setUnknownTags(unk);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  const subscribeRealtime = () => {
    const ch = supabase.channel('tracking-live')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'asset_movements',
        filter: `org_id=eq.${profile.org_id}` }, (payload) => {
        setAssetMovements(prev => [payload.new, ...prev.slice(0, 199)]);
        setLiveCount(n => n + 1);
        // Flash the feed
        if (feedRef.current) {
          feedRef.current.style.boxShadow = `0 0 0 2px ${ACCENT}`;
          setTimeout(() => { if (feedRef.current) feedRef.current.style.boxShadow = 'none'; }, 600);
        }
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'unknown_tags',
        filter: `org_id=eq.${profile.org_id}` }, () => {
        loadData();
      })
      .subscribe();
    return () => supabase.removeChannel(ch);
  };

  const fetchBridgeStatus = async () => {
    try {
      const res  = await fetch(`${bridgeUrl}/api/status`, { signal: AbortSignal.timeout(2000) });
      setBridgeStatus(await res.json());
    } catch(e) { setBridgeStatus(null); }
  };

  // Filter movements
  const filtered = useMemo(() => {
    let list = assetMovements;
    if (facilityFilter !== 'all') list = list.filter(m => m.to_facility === facilityFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(m =>
        m.asset_name?.toLowerCase().includes(q) ||
        m.epc?.toLowerCase().includes(q) ||
        m.to_facility_name?.toLowerCase().includes(q) ||
        m.reader_name?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [assetMovements, facilityFilter, search]);

  // Stats
  const uniqueAssetsToday = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return new Set(assetMovements.filter(m => m.detected_at?.startsWith(today)).map(m => m.asset_id)).size;
  }, [assetMovements]);

  const activeAntennas = antennas.filter(a => a.status === 'Online').length;

  const fmtTime = (ts) => {
    if (!ts) return '—';
    const d = new Date(ts);
    return d.toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const fmtAgo = (ts) => {
    if (!ts) return '';
    const sec = Math.floor((Date.now() - new Date(ts)) / 1000);
    if (sec < 60)  return `${sec}s ago`;
    if (sec < 3600) return `${Math.floor(sec/60)}m ago`;
    return `${Math.floor(sec/3600)}h ago`;
  };

  return (
    <div style={{ padding: '0 0 40px' }}>

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 22, color: '#e8edf2' }}>
          📡 Live RFID Tracking
        </div>
        <div style={{ fontSize: 12, color: '#7090a0', marginTop: 4 }}>
          Real-time asset movement feed · {assetMovements.length} events loaded
        </div>
      </div>

      {/* Bridge Status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
        background: '#060b10', border: `1px solid ${bridgeStatus ? '#00e5a030' : '#1a2a3a'}`,
        borderRadius: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%',
          background: bridgeStatus ? '#00e5a0' : '#e05757',
          boxShadow: bridgeStatus ? '0 0 8px #00e5a0' : 'none' }}/>
        <span style={{ fontSize: 12, fontWeight: 700, color: bridgeStatus ? '#00e5a0' : '#a0b4c0' }}>
          {bridgeStatus ? 'Bridge Online' : 'Bridge Offline'}
        </span>
        {bridgeStatus && (
          <>
            <span style={{ fontSize: 11, color: '#7090a0' }}>·</span>
            <span style={{ fontSize: 11, color: '#7090a0' }}>
              {bridgeStatus.connectedReaders} reader{bridgeStatus.connectedReaders !== 1 ? 's' : ''} connected
            </span>
            <span style={{ fontSize: 11, color: '#7090a0' }}>·</span>
            <span style={{ fontSize: 11, color: '#7090a0' }}>
              {bridgeStatus.tagsToday} tags today
            </span>
            {/* Reader chips */}
            {bridgeStatus.readers?.map(r => (
              <span key={r.key} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20,
                background: r.connected ? '#00e5a010' : '#e0575710',
                border: `1px solid ${r.connected ? '#00e5a030' : '#e0575730'}`,
                color: r.connected ? '#00e5a0' : '#e05757', fontWeight: 600 }}>
                {r.name} {r.connected ? `(${r.tagsRead || 0})` : '●'}
              </span>
            ))}
          </>
        )}
        {!bridgeStatus && (
          <span style={{ fontSize: 11, color: '#7090a0' }}>
            Start the RFID bridge app to enable live tracking
          </span>
        )}
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { icon: '🏷', label: 'Total Events',    value: assetMovements.length,  c: ACCENT },
          { icon: '📦', label: 'Assets Today',    value: uniqueAssetsToday,       c: '#5b9cf6' },
          { icon: '⚠',  label: 'Unknown Tags',    value: unknownTags.length,      c: unknownTags.length > 0 ? '#f5a623' : '#a0b4c0' },
          { icon: '📡', label: 'Online Antennas', value: activeAntennas,          c: activeAntennas > 0 ? ACCENT : '#e05757' },
        ].map(s => (
          <div key={s.label} style={{ background: '#060b10', border: '1px solid #1a2a3a', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontSize: 10, color: '#7090a0', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 6 }}>{s.icon} {s.label}</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: s.c, fontFamily: "'Syne',sans-serif" }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 16, alignItems: 'start' }}>

        {/* Live Feed */}
        <div>
          {/* Filters */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <input
              style={{ flex: 1, minWidth: 180, background: '#060b10', border: '1px solid #1a2a3a',
                borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#c8d6e0', outline: 'none' }}
              placeholder="Search EPC, asset name, facility…"
              value={search} onChange={e => handleSetSearch(e.target.value)}
            />
            <select style={{ background: '#060b10', border: '1px solid #1a2a3a', borderRadius: 8,
              padding: '8px 12px', fontSize: 12, color: '#c8d6e0', outline: 'none' }}
              value={facilityFilter} onChange={e => setFacilityFilter(e.target.value)}>
              <option value="all">All Facilities</option>
              {facilities.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
            <button style={{ background: '#1a2a3a', border: 'none', borderRadius: 8,
              padding: '8px 14px', fontSize: 12, color: '#c8d6e0', cursor: 'pointer' }}
              onClick={loadData}>↻ Refresh</button>
          </div>

          {/* Live indicator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: ACCENT,
              animation: bridgeStatus?.connectedReaders > 0 ? 'ping 1.5s infinite' : 'none' }}/>
            <span style={{ fontSize: 11, color: '#7090a0' }}>
              {bridgeStatus?.connectedReaders > 0 ? `Live — ${liveCount} events this session` : 'Showing historical data'}
            </span>
          </div>

          {/* Feed */}
          <div ref={feedRef} style={{ display: 'flex', flexDirection: 'column', gap: 6,
            flex: 1, overflowY: 'auto', transition: 'box-shadow 0.3s', borderRadius: 10, minHeight: 0 }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#7090a0' }}>Loading…</div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 0', color: '#7090a0' }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>📡</div>
                <div style={{ fontSize: 14, marginBottom: 6 }}>No movements yet</div>
                <div style={{ fontSize: 12 }}>Waiting for RFID tags to be detected…</div>
              </div>
            ) : (<>
              {filtered.slice(0, visibleCount).map((m, i) => (
                <MovementRow getAntennaName={getAntennaName} key={m.id || i} m={m} fmtTime={fmtTime} fmtAgo={fmtAgo} isNew={i === 0 && liveCount > 0} />
              ))}
              {filtered.length > visibleCount && (
                <button onClick={() => setVisibleCount(c => c + 50)}
                  style={{ margin:'8px 0 4px',padding:'10px',borderRadius:8,background:'#0d1821',border:'1px solid #1a2a3a',
                    color:'#a0b4c0',fontSize:12,cursor:'pointer',width:'100%',textAlign:'center' }}>
                  ⬇ Show more ({filtered.length - visibleCount} remaining)
                </button>
              )}
              {filtered.length > 0 && filtered.length <= visibleCount && (
                <div style={{ textAlign:'center',padding:'10px 0',fontSize:11,color:'#3a5060' }}>
                  — All {filtered.length} records shown —
                </div>
              )}
            </>)}
          </div>
        </div>

        {/* Right panel */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* Online Readers */}
          <div style={{ background: '#060b10', border: '1px solid #1a2a3a', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#e8edf2', marginBottom: 12 }}>📡 Antennas</div>
            {antennas.length === 0 ? (
              <div style={{ fontSize: 12, color: '#7090a0' }}>No antennas configured</div>
            ) : antennas.map(a => {
              const fac = facilities.find(f => String(f.id) === String(a.facility_id));
              return (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 0', borderBottom: '1px solid #0d1520' }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                    background: a.status === 'Online' ? ACCENT : '#e05757',
                    boxShadow: a.status === 'Online' ? `0 0 6px ${ACCENT}` : 'none' }}/>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#c8d6e0' }}>{a.location}</div>
                    <div style={{ fontSize: 10, color: '#7090a0', marginTop: 2 }}>ANT{a.antenna_port || '?'} · {a.facility_name || '—'}</div>
                    <div style={{ fontSize: 10, color: '#7090a0' }}>{fac?.name || '—'}</div>
                  </div>
                  <div style={{ fontSize: 10, color: '#7090a0', fontFamily: "'IBM Plex Mono',monospace" }}>
                    {(a.reads || 0).toLocaleString()}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Unknown Tags */}
          {unknownTags.length > 0 && (
            <div style={{ background: '#060b10', border: '1px solid #f5a62330', borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#f5a623' }}>⚠ Unknown Tags ({unknownTags.length})</div>
              </div>
              <div style={{ fontSize: 11, color: '#7090a0', marginBottom: 10 }}>
                Tags detected but not registered in WMS. Register them as assets to start tracking.
              </div>
              {unknownTags.slice(0, 8).map(t => {
                // Try to find the last movement for this EPC to get reader/antenna info
                const lastMove = movements.find(m => m.epc === t.epc);
                return (
                  <div key={t.id} style={{ display:'flex', alignItems:'center', gap:10,
                    padding: '8px 0', borderBottom: '1px solid #0d1520' }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize: 11, fontFamily: "'IBM Plex Mono',monospace", color: '#f5a623', marginBottom:3 }}>
                        {t.epc}
                      </div>
                      <div style={{ display:'flex', gap:10, fontSize: 10, color: '#7090a0', flexWrap:'wrap' }}>
                        <span>🔁 {t.read_count} reads</span>
                        <span>🕐 {fmtAgo(t.last_seen_at)}</span>
                        {lastMove?.reader_name && (
                          <span style={{ color:'#a0b4c0' }}>📡 {lastMove.reader_name}</span>
                        )}
                        {lastMove && (lastMove.antenna_name || lastMove.antenna_port) && (
                          <span style={{ color:'#00e5a0' }}>
                            📍 {lastMove.antenna_name || `ANT${lastMove.antenna_port}`}
                          </span>
                        )}
                        {lastMove?.to_facility_name && (
                          <span style={{ color:'#5b9cf6' }}>🏭 {lastMove.to_facility_name}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {unknownTags.length > 8 && (
                <div style={{ fontSize: 11, color: '#7090a0', marginTop: 8, textAlign: 'center' }}>
                  +{unknownTags.length - 8} more unknown tags
                </div>
              )}
            </div>
          )}

          {/* Asset locations snapshot */}
          <div style={{ background: '#060b10', border: '1px solid #1a2a3a', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#e8edf2', marginBottom: 12 }}>📦 Assets by Facility</div>
            {facilities.map(f => {
              const facAssets  = assets.filter(a => a.current_facility === f.id || a.facility_id === f.id);
              const facAnts    = antennas.filter(a => String(a.facility_id) === String(f.id));
              if (facAssets.length === 0) return null;
              return (
                <div key={f.id} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#c8d6e0' }}>{f.name}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: ACCENT,
                      fontFamily: "'IBM Plex Mono',monospace" }}>{facAssets.length} assets</div>
                  </div>
                  {facAnts.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {facAnts.map(ant => {
                        // Count assets last seen at this antenna
                        const atAnt = assetMovements.filter(m =>
                          m.to_facility === f.id && m.antenna_port === ant.antenna_port
                        ).length;
                        return (
                          <div key={ant.id} style={{ display: 'flex', alignItems: 'center', gap: 4,
                            padding: '3px 8px', borderRadius: 6,
                            background: ant.status === 'Online' ? '#00e5a010' : '#1a2a3a',
                            border: `1px solid ${ant.status === 'Online' ? '#00e5a030' : '#2a3a4a'}`,
                            fontSize: 10 }}>
                            <span style={{ color: '#c8d6e0', fontWeight:600 }}>{ant.location}</span>
                            <span style={{ fontFamily: "'IBM Plex Mono',monospace", color: '#7090a0', fontSize:10 }}>ANT{ant.antenna_port}</span>
                            {atAnt > 0 && <span style={{ color: ACCENT, fontWeight: 700 }}>{atAnt}</span>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            {assets.length === 0 && (
              <div style={{ fontSize: 12, color: '#7090a0' }}>No assets tracked yet</div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

function MovementRow({ m, fmtTime, fmtAgo, isNew, getAntennaName }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px',
      background: isNew ? 'rgba(0,229,160,0.05)' : '#060b10',
      border: `1px solid ${isNew ? '#00e5a040' : '#1a2a3a'}`,
      borderRadius: 8, transition: 'all 0.5s',
    }}>
      {/* Time */}
      <div style={{ flexShrink: 0, textAlign: 'right', minWidth: 52 }}>
        <div style={{ fontSize: 11, color: '#00e5a0', fontFamily: "'IBM Plex Mono',monospace", fontWeight: 700 }}>
          {fmtTime(m.detected_at)}
        </div>
        <div style={{ fontSize: 10, color: '#7090a0', marginTop: 2 }}>{fmtAgo(m.detected_at)}</div>
      </div>

      {/* Icon */}
      <div style={{ width: 32, height: 32, borderRadius: 8, background: '#1a2a3a',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, flexShrink: 0 }}>
        📦
      </div>

      {/* Details */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#e8edf2',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {m.asset_name || 'Unknown Asset'}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap', fontSize: 11, color: '#7090a0' }}>
          {m.from_facility && (
            <>
              <span>{m.from_facility_name || m.from_facility}</span>
              <span>→</span>
            </>
          )}
          <span style={{ color: '#5b9cf6', fontWeight: 600 }}>{m.to_facility_name || m.to_facility}</span>
          <span>·</span>
          <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10 }}>
            {m.epc?.slice(0, 16)}…
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 3, fontSize: 10, color: '#7090a0' }}>
          <span style={{ color:'#a0b4c0' }}>📡 {m.reader_name || '—'}</span>
          {m.antenna_port && (
            <span style={{ color: '#00e5a0', fontWeight: 600 }}>
              📍 {getAntennaName(m) || `ANT${m.antenna_port}`}
              {getAntennaName(m) && (
                <span style={{ color: '#7090a0', fontWeight: 400, marginLeft: 4 }}>
                  (ANT{m.antenna_port})
                </span>
              )}
            </span>
          )}
          {m.rssi && <span style={{ color:'#a0b4c0' }}>{m.rssi} dBm</span>}
        </div>
      </div>

      {/* Movement badge */}
      {m.from_facility && m.from_facility !== m.to_facility ? (
        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, flexShrink: 0,
          background: '#e0575718', border: '1px solid #e0575740', color: '#e05757' }}>
          ↔ MOVED
        </span>
      ) : (
        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, flexShrink: 0,
          background: '#00e5a010', border: '1px solid #00e5a030', color: '#00e5a0' }}>
          ● SEEN
        </span>
      )}
    </div>
  );
}
